import React, { useState } from 'react';
import axios from 'axios';
import { API_V1 } from '../lib/api';

const API_BASE_URL = API_V1;

const CHANGELOG = [
  { date: '2026-07-28', title: '论文摘要质量修复：核心摘要优先且完整展示', desc: '卡片摘要优先采用作者撰写的『核心摘要』原文并完整展示（不再截断 600 字），缺失或过短时回退 LLM 实质摘要；增强 LLM JSON 解析容错（非法转义、字段间缺失逗号），全量刷新补齐推荐理由与标签。' },
  { date: '2026-07-24', title: 'Agent 接入上线：Skill / RSS / REST API 三通道', desc: '发布 mulan-paper Skill 完整包、RSS 精选/全文/日报/主题 feed、匿名只读 API 与使用条款。' },
  { date: '2026-07-24', title: '极简风格改版', desc: '左侧栏导航可收缩、统一论文卡片、主题/收藏/搜索/分享、谷歌全文翻译与豆包解读。' },
  { date: '2026-07-23', title: '内容全量入库', desc: 'GitHub 内容仓库每日同步至站内数据库，478 篇解读全部带 LLM 中文摘要与推荐理由。' },
  { date: '2026-07-20', title: '合并 ai-paper-analysis 内容', desc: '日报 / 周报 / 主题 / 论文解读接入 ai-paper-analysis 仓库。' },
];

const TABS = [
  { key: 'author', label: '作者' },
  { key: 'changelog', label: '更新日志' },
  { key: 'feedback', label: '反馈' },
  { key: 'terms', label: '使用条款' },
];

const TERMS = [
  {
    intro: 'paper.mulanai.com 长期保留一档匿名、只读、无需 API Key 的基础公开 API，并继续提供稳定的 RSS 与 Agent Skill 接入。未来高配额或附加服务可以单独治理，但不会用它们替代基础层。',
  },
  {
    title: '可以怎样使用',
    items: [
      '允许个人项目、私人客户端和具有独立产品、采编、分析、去重或工作流价值的商业客户端使用。',
      '允许为客户端运行、离线阅读、去重和可靠性所需的合理私人缓存。',
      '公开展示木兰论文解读数据时，须保留响应中的木兰论文解读署名和 canonical 入口，并遵守公开限流、缓存与重试约定。',
    ],
  },
  {
    title: '不包含哪些授权',
    items: [
      '本条款不默认授权纯镜像、换皮站、批量公开再分发或以木兰论文解读数据替代本站的公开服务；如有此类需求，请先联系确认。',
      '第三方原文、图片和全文的权利不会因进入木兰论文解读而转移。全文 RSS 只按现有来源授权门禁输出，也不构成对第三方内容的再许可。',
      '不得绕过或协助绕过限流、缓存、安全措施和来源授权门禁。',
      '基础服务按现状尽力提供，不承诺 SLA。木兰论文解读可以为安全、成本和稳定性调整合理限流，但不会借此取消基础匿名层。',
    ],
  },
  {
    title: '兼容与迁移',
    items: [
      '进入稳定 major 版本的 API 不删除、改名或改变既有字段类型；可以增加可选字段，客户端应忽略未知字段并把 cursor 当作不透明字符串。',
      '/api/v1 在 major 版本内保持字段和路径兼容；RSS URL 与 GUID 长期稳定。旧未版本化接口按观察和通知机制迁移，不承诺永久保留。',
      '旧接口不会突然关闭。只有真实使用证据满足迁移门槛后才进入弃用，并在正式停止前提供明确迁移说明和至少 180 天通知。',
    ],
  },
];

export default function AboutPage() {
  const [tab, setTab] = useState('author');
  const [form, setForm] = useState({ email: '', message: '' });
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState('');

  function submit(e) {
    e.preventDefault();
    setErr('');
    if (!form.message.trim()) { setErr('请填写反馈内容'); return; }
    axios
      .post(`${API_BASE_URL}/feedback/`, { name: '', email: form.email, message: form.message, page: 'about' })
      .then(() => { setSent(true); setForm({ email: '', message: '' }); })
      .catch(() => setErr('提交失败，请稍后重试'));
  }

  return (
    <div className="page-about">
      <div className="page-head">
        <div className="page-title">关于</div>
        <div className="page-sub">木兰论文解读 · AI 论文中文解读与每日精选</div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            className={`btn${tab === t.key ? ' btn-primary' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'author' && (
        <div>
          <div className="about-hero">
            <div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>徐明 <span className="author-alias">（Xu Ming · shibing624）</span></div>
              <div className="author-badges">
                <span className="author-badge"><i className="fab fa-github" /> GitHub 29k+ Star</span>
                <span className="author-badge"><i className="fas fa-brain" /> 高级算法研究员 · NLP/LLM</span>
                <span className="author-badge"><i className="fas fa-briefcase" /> 百度|腾讯</span>
              </div>
              <div className="summary-text" style={{ marginTop: 8 }}>
                AI 算法研究员 / 全栈工程师。ACL/EMNLP审稿人。热衷开源与分享，运营{' '}
                <a href="https://github.com/shibing624/ai-paper-analysis" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-cyan)' }}>ai-paper-analysis</a>{' '}
                论文解读项目与本站：每日精选 AI 论文，给出信息密度拉满的中文解读，让前沿研究更好读、更好用。
              </div>
              <div className="author-links">
                <a href="https://github.com/shibing624" target="_blank" rel="noopener noreferrer">
                  <i className="fab fa-github" /> GitHub
                </a>
                <a href="https://scholar.google.com/citations?user=LhLTmtIAAAAJ&hl=zh-CN" target="_blank" rel="noopener noreferrer">
                  <i className="fas fa-graduation-cap" /> Google Scholar
                </a>
                <a href="https://github.com/shibing624/ai-paper-analysis" target="_blank" rel="noopener noreferrer">
                  <i className="fas fa-book-open" /> ai-paper-analysis
                </a>
              </div>
            </div>
          </div>

          <div className="author-quote">
            <i className="fas fa-quote-left" />
            知识是包不住的，卖力分享，才能进步。
          </div>

          <div className="contact-cards">
            <div className="contact-card">
              <div className="contact-title">
                <i className="fas fa-qrcode" /> 微信公众号
              </div>
              <div className="contact-name">机器懂语言</div>
              <img className="contact-qr" src="/wechat-mp.jpeg" alt="微信公众号：机器懂语言" loading="lazy" />
              <div className="contact-desc">扫码关注，获取论文解读更新</div>
            </div>

            <div className="contact-card">
              <div className="contact-title">
                <i className="fas fa-user" /> 个人微信
              </div>
              <div className="contact-name">xuming624</div>
              <img className="contact-qr" src="/wechat-personal.jpeg" alt="个人微信二维码" loading="lazy" />
              <div className="contact-desc">欢迎交流论文与工程问题</div>
            </div>

            <div className="contact-card">
              <div className="contact-title">
                <i className="fas fa-users" /> LLM 技术交流群
              </div>
              <img className="contact-qr" src="/wechat-group.jpg" alt="LLM 技术交流群二维码" loading="lazy" />
              <div className="contact-desc">
                群二维码 7 天内有效；过期请加个人微信 <b>xuming624</b>，备注 <b>“llm”</b>，拉你进群
              </div>
            </div>
          </div>

          <div className="author-thanks">如果觉得有点用，欢迎加入。</div>
        </div>
      )}

      {tab === 'changelog' && (
        <div>
          {CHANGELOG.map((c) => (
            <div key={c.date} className="changelog-item">
              <div className="changelog-date">{c.date}</div>
              <div className="changelog-title">{c.title}</div>
              <div className="summary-text">{c.desc}</div>
            </div>
          ))}
        </div>
      )}

      {tab === 'terms' && (
        <div id="public-integration-terms">
          <div className="feed-card" style={{ marginBottom: 14 }}>
            <h3 style={{ marginBottom: 8 }}>API、RSS 与 Agent Skill 使用条款</h3>
            <p className="summary-text">{TERMS[0].intro}</p>
          </div>
          {TERMS.slice(1).map((sec) => (
            <div key={sec.title} className="feed-card" style={{ marginBottom: 14 }}>
              <h3 style={{ marginBottom: 8 }}>{sec.title}</h3>
              <ul className="agent-ul">
                {sec.items.map((it) => <li key={it}>{it}</li>)}
              </ul>
            </div>
          ))}
        </div>
      )}

      {tab === 'feedback' && (
        <div className="feed-card">
          {sent ? (
            <div className="empty-state" style={{ padding: 30 }}>
              <i className="fas fa-check-circle" style={{ color: 'var(--accent-emerald)' }} />
              <div>感谢反馈，已收到！</div>
              <button className="btn" style={{ marginTop: 12 }} onClick={() => setSent(false)}>再写一条</button>
            </div>
          ) : (
            <form onSubmit={submit}>
              <textarea
                className="field"
                rows={6}
                maxLength={2000}
                placeholder="你想说的话（限 2000 字符）…"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
              />
              <div className="field-counter">{form.message.length} / 2000</div>
              <input
                className="field"
                type="email"
                placeholder="邮箱（选填，方便回复你）"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
              {err && <div className="error-banner" style={{ marginTop: 12 }}>{err}</div>}
              <button className="btn btn-primary" type="submit" style={{ marginTop: 14 }}>
                <i className="fas fa-paper-plane" />提交反馈
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
